// * relative
import _ from "lodash";
import { connect } from "react-redux";
import React, {
  useRef,
  useMemo,
  useCallback,
  useEffect,
  useState,
} from "react";
import { Button, IconButton, Tooltip } from "@toddle-edu/ds-web";
import classnames from "classnames";
import {
  AddOutlined,
  AssessmentAddOutlined,
  NoteOutlined,
} from "@toddle-edu/ds-icons";

// * internal
import AiQuestionAssistant from "ClassRoom/components/AddAssignment/AiQuestionAssistant/AiQuestionAssistant";
import Loading from "UIComponents/Loading";
import { getIsFromClassroom } from "Teacher/routes/Courses/routes/Course/routes/UnitPlans/modules/Utils";
import { createAIConversation } from "ClassRoom/components/AddAssignment/AiQuestionAssistant/modules/AiQuestionAssistantModule";
import {
  PLANNER,
  TODDLE_LIBRARY,
  WORKSHEET_QUESTION_CREATION,
} from "Constants/stringConstants";
import { getPlannerTemplateFromCache } from "modules/CommonGraphqlHelpers";
import {
  FIELD_UID_ADD_LEARNING_GOALS,
  WORKSHEET_ELEMENT_IDS,
  QUESTION_UID_SEPARATOR,
  FIELD_UID_TITLE,
} from "Worksheet/modules/WorksheetConstants";
import { getAddLearningGoalsDropdownOptions } from "UnitPlans/components/LibraryCommonComponents/ResourceEdit/modules/Utils";
import { getWorksheetAssistantContainerStyle } from "ClassRoom/components/AddAssignment/AiQuestionAssistant/modules/Utils";
import {
  updateAddQuestionsInBetween,
  updateAIAssessmentTools,
  updateLoadingState,
  updateVisibleQuestionAssistantIds,
  updateWorksheetSidebar,
} from "Worksheet/modules/WorksheetModules";
import {
  getOptionsFromField,
  getFormattedQuestion,
  getLearningGoalsFields,
  getAssessmentDetailsData,
} from "Worksheet/modules/WorksheetUtils";
import {
  getDynamicTemplate,
  updateAssessmentTemplate,
} from "Teacher/routes/Courses/routes/Course/routes/UnitPlans/components/Assessments/modules/Utils";
import {
  getUserType,
  getActiveTab,
  getSelectedCurriculumId,
  getSelectedAcademicYearId,
  getCurrentCurriculumProgramType,
} from "modules/CommonSelectors";

// * relative
import SectionHeader from "./SectionHeader";
import QuestionDetails from "../QuestionDetails";
import classes from "./SinglePageQuestionEditor.scss";
import SinglePageScrollController from "./SinglePageScrollController";
import {
  getSectionDetails,
  getQuestionIndex,
  getActiveQuestion,
  getQuestionInfoForAiAssistant,
  getLearningGoalsFieldsData,
} from "./SinglePageQuestionEditorUtils";
import AIAssessmentTools from "../../AIAssessmentTools";

const InstructionAndToolsController = ({
  t,
  hasInstruction,
  hasAssessmentTools,
  worksheetBasicDetailInfo,
  setWorksheetBasicDetailsInfo,
}) => {
  const { showInstructions, showAssessmentTools } = worksheetBasicDetailInfo;
  if (showInstructions && showAssessmentTools) return null;
  if (hasAssessmentTools && hasInstruction) return null;
  return (
    <div className={classes.instructionAndToolsController}>
      {!showInstructions && !hasInstruction && (
        <Tooltip
          tooltip={t("worksheet:worksheet_instructions")}
          placement={"top"}
          showArrow={true}
        >
          <IconButton
            variant={"outlined-subtle"}
            size="large"
            icon={<NoteOutlined />}
            testId="worksheet-basic-details-instruction-icon"
            onClick={() =>
              setWorksheetBasicDetailsInfo({
                ...worksheetBasicDetailInfo,
                showInstructions: true,
              })
            }
          />
        </Tooltip>
      )}
      {!showAssessmentTools && !hasAssessmentTools && (
        <Tooltip
          tooltip={t("classRoom:assessment_tools")}
          placement={"top"}
          showArrow={true}
        >
          <IconButton
            variant={"outlined-subtle"}
            size="large"
            icon={<AssessmentAddOutlined />}
            testId="worksheet-basic-details-assessment-tools-icon"
            onClick={() =>
              setWorksheetBasicDetailsInfo({
                ...worksheetBasicDetailInfo,
                showAssessmentTools: true,
              })
            }
          />
        </Tooltip>
      )}
    </div>
  );
};

/**
 *
 *
 *
 * @param {*} params
 * @returns It renders question in single list as single page UI and we maintain the active question
 */
const SinglePageQuestionEditor = ({
  t,
  mode,
  setQuestionRef,
  showWorksheetAssistant,
  classSubjects,
  parentId,
  portalType,
  creationMode,
  unitPlanTemplateId,
  getLearningGoalsMappingWrapper,
  worksheetDetailsRef,
  courseId,
  selectedCourse,
  unitPlanId,
  isFromUnitPlan,
  isDraft,
  sourceFrom,
  sections,
  activeQuestionId,
  createAIConversation,
  updateVisibleQuestionAssistantIds,
  learningGoalsDropdownOptions,
  selectedQuestionFields,
  updateLoadingState,
  questionAIConversationId,
  getAddButton,
  isGeneratingAIQuestions,
  updateWorksheetSidebar,
  assessmentDetailsComponent,
  updateAddQuestionsInBetween,
  nextQuestionResourceId,
  hasAssessmentTools,
  hasInstruction,
  isAIAssessmentToolsOpen,
  assessmentToolConversationId,
  assessmentTools,
  assessmentDetails,
  getAssessmentRefetch,
  updateAIAssessmentTools,
}) => {
  const containerRef = useRef(null);
  const spaceContainerInBetweenRef = useRef(null);
  const [addButtonActiveIds, setAddButtonActiveIds] = useState([]);
  const [addButtonDropdownActiveId, setAddButtonDropdownActiveId] = useState(
    null
  );
  const hasSections = !_.isEmpty(sections);
  const [
    worksheetBasicDetailInfo,
    setWorksheetBasicDetailsInfo,
  ] = React.useState({
    showInstructions: false,
    showAssessmentTools: false,
  });

  useEffect(() => {
    if (activeQuestionId === WORKSHEET_QUESTION_CREATION) {
      // * Scroll to the top when we're on the question creation view
      if (containerRef.current) {
        containerRef.current.scrollTo({
          top: 0,
          behavior: "smooth",
        });
      }
    }
  }, [activeQuestionId]);

  // * Getting the active question details for the question assistant
  const questionInfoForAssistant = useMemo(
    () => getQuestionInfoForAiAssistant(t, activeQuestionId, sections),
    [t, activeQuestionId, sections]
  );

  // * Handling the show/hide of question assistant and create ai conversation
  const handleShowQuestionAssistant = () => {
    // * Closing the question assistant if already showing or not an inactive question
    if (showWorksheetAssistant || !activeQuestionId) {
      updateVisibleQuestionAssistantIds([]);
      // toggling sidebar on smaller screens for better visibility when AiQuestionAssistant is opened
      if (window.innerWidth < 1150) {
        updateWorksheetSidebar({ isOpen: true });
      }
      return;
    }

    // * Finding Question assistant AI Conversation ID
    const { questionAIConversationId } = getActiveQuestion(
      sections,
      activeQuestionId
    );

    // * Checking and creating AI conversation if not exists
    if (_.isEmpty(questionAIConversationId)) {
      // * Getting learning goals field data
      const learningGoalsFieldsData = getLearningGoalsFieldsData(
        selectedQuestionFields,
        learningGoalsDropdownOptions
      );

      // * Creating ai conversation
      createAIConversation({
        input: {
          entityType: "QUESTION",
          entityId: activeQuestionId,
          metadata: { allFields: learningGoalsFieldsData, localFields: [] },
        },
        isQuestionAssistant: true,
      });
    }

    // * Updating the visible question assistant ids
    updateVisibleQuestionAssistantIds([activeQuestionId]);
    // toggling sidebar on smaller screens for better visibility when AiQuestionAssistant is opened
    if (window.innerWidth < 1150) {
      updateWorksheetSidebar({ isOpen: false });
    }
  };

  // * Showing full screen loader controlled by assistant
  const showFullScreenLoader = useCallback(state => updateLoadingState(state), [
    updateLoadingState,
  ]);

  const newWorksheetBasicDetailManager = {
    worksheetBasicDetailInfo,
    setWorksheetBasicDetailsInfo,
  };

  const getInstructionAndToolsController = useCallback(
    () => (
      <InstructionAndToolsController
        t={t}
        hasInstruction={hasInstruction}
        hasAssessmentTools={hasAssessmentTools}
        worksheetBasicDetailInfo={worksheetBasicDetailInfo}
        setWorksheetBasicDetailsInfo={setWorksheetBasicDetailsInfo}
      />
    ),
    [
      t,
      worksheetBasicDetailInfo,
      setWorksheetBasicDetailsInfo,
      hasAssessmentTools,
      hasInstruction,
    ]
  );

  const handleAddQuestionsInBetween = ({
    questionResourceId,
    parentResourceId,
    nextQuestionResourceId,
    isAddingAtStartOfMultipart = false,
    questionId,
    parentItemId,
  }) => {
    let _parentResourceId = parentResourceId;
    // Distinguish start-of-multipart from after-multipart (same resource id) to avoid toggle conflict
    if (isAddingAtStartOfMultipart) {
      _parentResourceId += "multipart";
    }
    setAddButtonActiveIds(prev => [
      ...prev,
      questionResourceId ?? _parentResourceId,
    ]);
    updateAddQuestionsInBetween({
      isAddingQuestionsInBetween: true,
      isAddingAtStartOfMultipart,
      questionResourceId,
      parentResourceId,
      nextQuestionResourceId,
      questionId,
      parentItemId,
    });
  };

  const onMouseLeave = ({ resourceId }) => {
    if (addButtonDropdownActiveId === resourceId) return;
    setAddButtonActiveIds(prev => prev.filter(id => id !== resourceId));
  };

  const onBlur = ({ e, resourceId }) => {
    if (e.currentTarget.contains(e.relatedTarget)) return;
    setAddButtonActiveIds(prev => prev.filter(id => id !== resourceId));
  };

  const singlePageQuestionEditorClasses = classnames({
    [classes.singlePageQuestionEditor]: true,
    [classes.singlePageQuestionEditorV2]: !isGeneratingAIQuestions,
    [classes.singlePageQuestionEditorV3]: true,
  });

  const handleCloseAIAssessmentTools = () => {
    updateAIAssessmentTools({ isOpen: false });
    // toggling sidebar on smaller screens for better visibility when AIAssesmentTools is closed
    if (window.innerWidth < 1192) {
      updateWorksheetSidebar({ isOpen: true });
    }
  };

  const showAddButton = !(
    isGeneratingAIQuestions ||
    isAIAssessmentToolsOpen ||
    showWorksheetAssistant
  );

  return (
    <>
      <div
        id={WORKSHEET_ELEMENT_IDS.WORKSHEET_SINGLE_PAGE_CONTAINER}
        ref={containerRef}
        className={classes.container}
      >
        <div className={singlePageQuestionEditorClasses}>
          {/* WORKSHEET BASIC DETAILS */}
          <div className={classes.assessmentDetailCompContainer}>
            <div className={classes.assessmentDetailCompContentContainer}>
              {React.cloneElement(assessmentDetailsComponent, {
                newWorksheetBasicDetailManager,
                footerComponent: null,
                customClassForStep: classes.customClassForStep,
              })}
            </div>
          </div>

          {/*  WORKSHEET ASSESSMENT TOOLS > TOGGLE BUTTONS */}
          {!isGeneratingAIQuestions && (
            <div className={classes.showAddDetailsButton}>
              {!hasInstruction && !worksheetBasicDetailInfo.showInstructions && (
                <Button
                  size="large"
                  type="outlined"
                  dsVersion="2.0"
                  variant="neutral"
                  className={classes.instructionAndToolsButton}
                  onClick={() =>
                    setWorksheetBasicDetailsInfo({
                      ...worksheetBasicDetailInfo,
                      showInstructions: true,
                    })
                  }
                  testId="worksheet-basic-details-instruction-icon"
                  icon={<NoteOutlined />}
                >
                  {t("common:add_with_label", {
                    label: t("common:instruction"),
                  })}
                </Button>
              )}
              {!hasAssessmentTools &&
                !worksheetBasicDetailInfo.showAssessmentTools && (
                  <Button
                    size="large"
                    type="outlined"
                    dsVersion="2.0"
                    variant="neutral"
                    className={classes.instructionAndToolsButton}
                    onClick={() =>
                      setWorksheetBasicDetailsInfo({
                        ...worksheetBasicDetailInfo,
                        showAssessmentTools: true,
                      })
                    }
                    testId="worksheet-basic-details-assessment-tools-icon"
                    icon={<AssessmentAddOutlined />}
                  >
                    {t("common:add_with_label", {
                      label: t("worksheet:assessment_tool"),
                    })}
                  </Button>
                )}
            </div>
          )}

          {hasSections && <div className={classes.contentSeparator} />}

          {/* LIST OF SECTIONS AND QUESTIONS */}
          {_.map(sections, (section, sectionIdx) => {
            const sectionId = _.get(section, "id", null);
            const parentItemId = _.get(section, "item.id", null);
            const { sectionScore, numberOfQuestions } = getSectionDetails(
              section
            );
            const isLastSection = sectionIdx === _.size(sections) - 1;
            let lastQuestionResourceId = null;
            if (isLastSection && !_.isEmpty(section)) {
              const lastQuestion = _.last(
                _.get(section, "childrenV2.edges", {})
              );
              lastQuestionResourceId = _.get(lastQuestion, "node.id", "");
            }
            const questions = _.get(section, "childrenV2.edges", []);

            const showAddQuestionAtTop =
              !isGeneratingAIQuestions &&
              (!isLastSection || !_.isEmpty(questions));

            const addQuestionsInBetweenProps = {
              forSidebar: false,
              isRevampedSinglePageUI: true,
              addButtonActiveIds,
              showAddSection: false,
              showAddSeparator: true,
              showAddInlineText: true,
              isAddDropdownButtonRevamp: true,
              btnComponent: (
                <div className={classes.addInBetweenIcon}>
                  <IconButton
                    icon={<AddOutlined />}
                    variant="neutral"
                    size="small"
                    type="outlined"
                    dsVersion="2.0"
                    disabled={isGeneratingAIQuestions}
                    testId="worksheet-addQuestionsDropdown-addQuestionsInBetween"
                  />
                </div>
              ),
              handleAddQuestionsInBetween: args =>
                handleAddQuestionsInBetween(args),
              onMouseLeave: args => onMouseLeave(args),
              onBlur: args => onBlur(args),
              setAddButtonDropdownActiveId,
              addButtonDropdownActiveId,
            };

            const titleFieldId = _.get(
              _.find(_.get(section, "item.fields", []), {
                uid: FIELD_UID_TITLE,
              }),
              "id",
              null
            );

            const firstQuestionResourceId = _.get(
              _.first(questions, {}),
              "node.id",
              ""
            );

            const isAddButtonActive = _.includes(addButtonActiveIds, sectionId);

            const addInBetweenClasses = classnames({
              [classes.addInBetween]: true,
              [classes.addInBetweenActive]: isAddButtonActive,
              [classes.addInBetweenDropdownActive]:
                addButtonDropdownActiveId === sectionId,
              [classes.disabledAddInBetween]:
                showWorksheetAssistant || isAIAssessmentToolsOpen,
            });

            const addQuestionsInBetweenPropsToOmit = [
              "handleAddQuestionsInBetween",
              "onMouseLeave",
              "onBlur",
              "addButtonActiveIds",
              "addButtonDropdownActiveId",
            ];

            return (
              <React.Fragment key={sectionId}>
                <SectionHeader
                  mode={mode}
                  id={sectionId}
                  label={_.get(section, "label")}
                  sectionScore={sectionScore}
                  numberOfQuestions={numberOfQuestions}
                  sectionMarks={0}
                  isGeneratingAIQuestions={isGeneratingAIQuestions}
                  parentItemId={parentItemId}
                  titleFieldId={titleFieldId}
                />
                {showAddQuestionAtTop && (
                  <div
                    className={addInBetweenClasses}
                    onMouseEnter={() =>
                      handleAddQuestionsInBetween({
                        questionResourceId: null,
                        parentResourceId: sectionId,
                        nextQuestionResourceId: firstQuestionResourceId,
                        questionId: null,
                        parentItemId,
                      })
                    }
                    onMouseLeave={() => onMouseLeave({ resourceId: sectionId })}
                    onBlur={e => onBlur({ e, resourceId: sectionId })}
                    id={
                      WORKSHEET_ELEMENT_IDS.WORKSHEET_ADD_QUESTION_IN_BETWEEN_ID
                    }
                  >
                    {isAddButtonActive &&
                      getAddButton({
                        ..._.omit(addQuestionsInBetweenProps, [
                          ...addQuestionsInBetweenPropsToOmit,
                        ]),
                      })}
                  </div>
                )}
                {_.map(questions, (question, index, allQuestions) => {
                  const questionId = _.get(question, "node.item.id", "");
                  const questionTypeUid = _.get(
                    question,
                    "node.item.questionType.uid",
                    ""
                  );
                  const questionResourceId = _.get(question, "node.id", "");
                  const questionIndex = getQuestionIndex(
                    sections,
                    questionResourceId
                  );
                  const isActive = questionId === activeQuestionId;

                  const isLastSectionLastQuestion =
                    questionResourceId === lastQuestionResourceId;
                  const showAddQuestionInBetween =
                    !isLastSectionLastQuestion && !isGeneratingAIQuestions;

                  // Get the next question's resourceId
                  const nextQuestion = allQuestions[index + 1];
                  const _nextQuestionResourceId = _.get(
                    nextQuestion,
                    "node.id",
                    ""
                  );

                  const spaceContainerInBetween =
                    questionResourceId === nextQuestionResourceId &&
                    isGeneratingAIQuestions;

                  const isAddButtonActive = _.includes(
                    addButtonActiveIds,
                    questionResourceId
                  );

                  const addInBetweenClasses = classnames({
                    [classes.addInBetween]: true,
                    [classes.addInBetweenActive]: isAddButtonActive,
                    [classes.addInBetweenDropdownActive]:
                      addButtonDropdownActiveId === questionResourceId,
                    [classes.disabledAddInBetween]:
                      showWorksheetAssistant || isAIAssessmentToolsOpen,
                  });

                  if (questionIndex === -1) return null;

                  return (
                    <React.Fragment key={questionId}>
                      <QuestionDetails
                        // Common props
                        t={t}
                        mode={mode}
                        key={questionId}
                        isDraft={isDraft}
                        courseId={courseId}
                        portalType={PLANNER}
                        sourceFrom={sourceFrom}
                        assessmentId={parentId}
                        unitPlanId={unitPlanId}
                        creationMode={creationMode}
                        classSubjects={classSubjects}
                        setQuestionRef={setQuestionRef}
                        selectedCourse={selectedCourse}
                        isFromUnitPlan={isFromUnitPlan}
                        unitPlanTemplateId={unitPlanTemplateId}
                        worksheetDetailsRef={worksheetDetailsRef}
                        showWorksheetAssistant={showWorksheetAssistant}
                        getLearningGoalsMappingWrapper={
                          getLearningGoalsMappingWrapper
                        }
                        // Question specific props
                        isActive={isActive}
                        sectionId={sectionId}
                        parentItemId={parentItemId} // section's item id
                        questionIndex={questionIndex}
                        questionResourceId={questionResourceId}
                        isAIAssessmentToolsOpen={isAIAssessmentToolsOpen}
                        isSeparatorQuestion={
                          questionTypeUid === QUESTION_UID_SEPARATOR
                        }
                        {...(spaceContainerInBetween && {
                          spaceContainerInBetweenRef,
                        })}
                        getAddButton={getAddButton}
                        addQuestionsInBetweenProps={addQuestionsInBetweenProps}
                      />
                      {showAddQuestionInBetween && (
                        <div
                          className={addInBetweenClasses}
                          onMouseEnter={() =>
                            handleAddQuestionsInBetween({
                              questionResourceId,
                              parentResourceId: sectionId,
                              nextQuestionResourceId: _nextQuestionResourceId,
                              questionId,
                              parentItemId,
                            })
                          }
                          onMouseLeave={() =>
                            onMouseLeave({ resourceId: questionResourceId })
                          }
                          onBlur={e =>
                            onBlur({ e, resourceId: questionResourceId })
                          }
                          id={
                            WORKSHEET_ELEMENT_IDS.WORKSHEET_ADD_QUESTION_IN_BETWEEN_ID
                          }
                        >
                          {isAddButtonActive &&
                            getAddButton({
                              ..._.omit(addQuestionsInBetweenProps, [
                                ...addQuestionsInBetweenPropsToOmit,
                              ]),
                            })}
                        </div>
                      )}
                    </React.Fragment>
                  );
                })}
              </React.Fragment>
            );
          })}

          {/* BUTTON TO ADD QUESTIONS AND SECTION */}
          {showAddButton ? (
            <div className={classes.addQuestionButtonContainer}>
              <div className={classes.addQuestionButton}>
                {getAddButton({
                  forSidebar: true,
                  isRevampedSinglePageUI: true,
                  showAddInlineText: true,
                  btnComponent: (
                    <Button
                      icon={<AddOutlined />}
                      variant="neutral"
                      type="outlined"
                      size="large"
                      isFullWidth={true}
                      dsVersion="2.0"
                      testId="worksheet-addQuestionsDropdown-singlePageEditor"
                    >
                      {t("common:add_with_label", {
                        label: `${t("worksheet:questions")}/${t(
                          "common:section"
                        )}`,
                      })}
                    </Button>
                  ),
                })}
              </div>
            </div>
          ) : activeQuestionId !== WORKSHEET_QUESTION_CREATION &&
            isGeneratingAIQuestions ? (
            <SinglePageScrollController
              containerRef={containerRef}
              activeQuestionId={activeQuestionId}
              isGeneratingAIQuestions={isGeneratingAIQuestions}
              spaceContainerInBetweenRef={spaceContainerInBetweenRef}
            />
          ) : null}
        </div>
      </div>

      {/* WORKSHEET QUESTION ASSISTANT */}
      {showWorksheetAssistant && (
        <div className={classes.aiAssistantWrapper}>
          {_.isEmpty(questionAIConversationId) ? (
            <Loading
              containerStyle={getWorksheetAssistantContainerStyle(true)}
            />
          ) : (
            <AiQuestionAssistant
              mode="edit"
              leId={parentId}
              isDraft={isDraft}
              courseId={courseId}
              key={activeQuestionId}
              unitPlanId={unitPlanId}
              sourceFrom={sourceFrom}
              portalType={portalType}
              isQuestionAssistant={true}
              creationMode={creationMode}
              selectedCourse={selectedCourse}
              isFromUnitPlan={isFromUnitPlan}
              question={questionInfoForAssistant}
              middlePaneRef={worksheetDetailsRef}
              setShowAssistantRightPane={handleShowQuestionAssistant}
              setAssessmentFullScreenLoader={showFullScreenLoader}
            />
          )}
        </div>
      )}

      {isAIAssessmentToolsOpen && (
        <div
          className={classes.aiAssistantWrapper}
          id={WORKSHEET_ELEMENT_IDS.WORKSHEET_AI_ASSESSMENT_TOOLS_ID}
        >
          <AIAssessmentTools
            mode="edit"
            isDraft={isDraft}
            courseId={courseId}
            entityType="WORKSHEET"
            portalType={portalType}
            sourceFrom={sourceFrom}
            assessmentId={parentId}
            unitPlanId={unitPlanId}
            creationMode={creationMode}
            isQuestionAssistant={false}
            isFromUnitPlan={isFromUnitPlan}
            selectedCourse={selectedCourse}
            assessmentTools={assessmentTools}
            middlePaneRef={worksheetDetailsRef}
            assessmentDetails={assessmentDetails}
            onClose={handleCloseAIAssessmentTools}
            getAssessmentRefetch={getAssessmentRefetch}
            aiConversationId={assessmentToolConversationId}
            assessmentToolConversationId={assessmentToolConversationId}
          />
        </div>
      )}
    </>
  );
};

const mapStateToProps = (state, ownProps) => {
  const {
    t,
    mode,
    sections,
    sourceFrom,
    assessmentId,
    creationMode,
    classSubjects,
    unitPlanTemplateId,
  } = ownProps;

  const activeQuestionId = _.get(state, "worksheet.activeQuestion");

  const isGenWithAIEnabled = _.get(
    state,
    "worksheet.isAIQuestionGenerationEnabled",
    false
  );

  const isGeneratingAIQuestions = _.get(
    state,
    "worksheet.generatingAIQuestions",
    false
  );

  const visibleQuestionAssistantIds = _.get(
    state,
    "worksheet.visibleQuestionAssistantIds",
    []
  );

  const isAIAssessmentToolsOpen = _.get(
    state,
    "worksheet.aiAssessmentTools.isOpen",
    false
  );

  const showWorksheetAssistant = _.includes(
    visibleQuestionAssistantIds,
    activeQuestionId
  );

  // * Getting learning goals dropdown options
  const {
    questionAIConversationId,
    question: activeQuestionData,
    questionIndex: activeQuestionIndex,
  } = getActiveQuestion(sections, activeQuestionId);

  const activeSelectedQuestion =
    !_.isEmpty(activeQuestionData) &&
    getFormattedQuestion({
      mode,
      question: _.get(activeQuestionData, "node", {}),
      questionIndex: activeQuestionIndex,
      subQuestions: _.map(
        _.get(activeQuestionData, "childrenV2.edges", []),
        "node"
      ),
      t,
    });

  const selectedQuestionFields = _.get(
    activeSelectedQuestion,
    "questionFields",
    []
  );

  const academicYearId = getSelectedAcademicYearId(state);
  const collaboratorsFilter = {
    userAcademicYearId: academicYearId,
  };

  const {
    assessmentType,
    worksheetTemplateId,
    unitType,
    unitPlanField,
    subjectsField,
    assessmentFields,
    assessmentDetails,
  } = getAssessmentDetailsData({ assessmentId, collaboratorsFilter });

  // * Check if the assessment has instructions
  const description = _.find(assessmentFields, { uid: "description" });
  const hasInstruction = !_.isEmpty(_.get(description, "value"));

  // * Check if the assessment has assessment tools other than SCORE(this is default)
  const assessmentTools = _.filter(
    _.get(assessmentDetails, "assessmentTools.edges"),
    item =>
      _.get(item, "node.assessmentToolType") !== "SCORE" &&
      _.get(item, "node.assessmentToolType") !== "COMMENT"
  );

  const hasAssessmentTools = !_.isEmpty(assessmentTools);

  const worksheetTemplateCacheData = getPlannerTemplateFromCache(
    { ids: [worksheetTemplateId] },
    false
  );

  const unitPlanTemplateCacheData = getPlannerTemplateFromCache(
    { ids: [unitPlanTemplateId] },
    false
  );

  const worksheetTemplate = _.get(worksheetTemplateCacheData, "body", {});
  const unitPlanTemplate = _.get(unitPlanTemplateCacheData, "body", {});

  const isFromClassroom = getIsFromClassroom({
    sourceFrom,
    template: worksheetTemplate,
  });

  const isToddleLibrary = getActiveTab(state) == TODDLE_LIBRARY;

  const curriculumType = isToddleLibrary
    ? _.get(state, "classRoom.curriculumProgram.type", "")
    : getCurrentCurriculumProgramType(state);

  const plannerElementSets = _.get(
    state,
    "platform.currentPlannerElementSets",
    []
  );

  const originalTemplate = getDynamicTemplate({
    template: worksheetTemplate,
    unitType,
    t,
    curriculumType,
    plannerElementSets,
    subjects: classSubjects,
    creationMode,
    curriculumId: getSelectedCurriculumId(state),
    templateId: worksheetTemplateId,
  });

  /** unit plan & subjects field needed for show unit/subjects based data in learning goals */
  const resourceFields = [
    ...selectedQuestionFields,
    unitPlanField,
    subjectsField,
  ];

  const updatedTemplate = updateAssessmentTemplate({
    t,
    template: originalTemplate,
    assessmentType,
    isFromClassroom,
    curriculumType,
    resourceFields,
    mode: "edit",
    templateId: worksheetTemplateId,
    plannerElementSets,
    subjects: classSubjects,
    userType: getUserType(state),
    assessmentTools: _.get(assessmentDetails, "assessmentTools", []),
  });

  const fieldList = _.get(updatedTemplate, "field_list", {});

  const defaultOptions = getOptionsFromField({
    fieldList,
    fieldUid: FIELD_UID_ADD_LEARNING_GOALS,
    t,
  });

  const learningGoalsDropdownOptions = getAddLearningGoalsDropdownOptions({
    options: defaultOptions,
    resourceFields,
    unitPlanTemplate,
    subjects: classSubjects,
    fieldList,
  });

  const learningGoalsFields = getLearningGoalsFields(updatedTemplate);

  const nextQuestionResourceId = _.get(
    state,
    "worksheet.addQuestionsInBetween.nextQuestionResourceId",
    null
  );

  return {
    activeQuestionId,
    isGenWithAIEnabled,
    isGeneratingAIQuestions,
    learningGoalsFields,
    selectedQuestionFields,
    showWorksheetAssistant,
    questionAIConversationId,
    learningGoalsDropdownOptions,
    nextQuestionResourceId,
    hasAssessmentTools,
    hasInstruction,
    isAIAssessmentToolsOpen,
  };
};

const mapDispatchToProps = {
  updateLoadingState,
  createAIConversation,
  updateVisibleQuestionAssistantIds,
  updateWorksheetSidebar,
  updateAddQuestionsInBetween,
  updateAIAssessmentTools,
};

export default connect(
  mapStateToProps,
  mapDispatchToProps
)(SinglePageQuestionEditor);
